import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  UserPlus, CheckCircle2, AlertCircle, Eye, EyeOff, ArrowRight, Lock, Loader2,
  User, Mail, Phone, MapPin, Linkedin, Globe, Languages, Mic, Briefcase, ShieldCheck, Sparkles,
} from 'lucide-react';
import { api, absoluteUrl } from '../lib/api';
import logo from '../assets/logo.png';
import { TagSelect } from './RegisterConfirmPage';
import InterpreterProfilePreview from '../components/InterpreterProfilePreview';
import FullProfilePreview from '../components/FullProfilePreview';
import RichTextEditor from '../components/RichTextEditor';
import CertificationsField from '../components/CertificationsField';
import ImageUploader from '../components/ImageUploader';
import SupportContactButton from '../components/SupportContactButton';
import { useClickLog } from '../hooks/useClickLog';

const Field = ({ label, icon: Icon, required, ...props }) => (
  <div>
    <label className="block text-xs font-semibold text-charcoal-700 mb-1.5 flex items-center gap-1.5">
      {Icon && <Icon size={13} className="text-cmic-500" />}{label}{required && <span className="text-rose-500">*</span>}
    </label>
    <input {...props} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
  </div>
);

const Card = ({ title, n, subtitle, children }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-6">
    <div className="flex items-start gap-3 mb-4">
      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-cmic-600 text-white text-xs font-bold flex items-center justify-center">{n}</span>
      <div>
        <h3 className="font-semibold text-charcoal-800 leading-tight">{title}</h3>
        {subtitle && <p className="text-xs text-charcoal-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

const EMPTY = {
  nombres: '', apellido_paterno: '', apellido_materno: '',
  email: '', phone: '', city: '', linkedin_url: '', website: '', bio: '', photo_url: '', references_text: '',
  additional_emails: [], additional_phones: [], excluded_language_pairs: [],
  active_languages: [], passive_languages: [], specialty: [], modalities: [],
  username: '', password: '', confirm: '',
};

export default function InvitationAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'loading' });
  const [form, setForm] = useState(EMPTY);
  const [catalogs, setCatalogs] = useState({ languages: [], specialties: [], modalities: [] });
  const [showPwd, setShowPwd] = useState(false);
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [restoredAt, setRestoredAt] = useState(null);
  const [usernameCheck, setUsernameCheck] = useState({ checking: false, available: null, suggestions: [] });
  const { logRef, log, onClickCapture } = useClickLog();
  const DRAFT_KEY = token ? `cmic_invite_draft_v1_${token}` : null;

  useEffect(() => { log('page_load', { page: 'invitation-accept' }); }, [log]);
  useEffect(() => { if (error) log('error', { message: error }); }, [error, log]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    let alive = true;
    api.get(`/invitations/${token}`)
      .then(({ data }) => {
        if (!alive) return;
        const p = data.profile || {};
        let { nombres, apellido_paterno, apellido_materno } = p;
        if (!nombres && !apellido_paterno && data.interpreter_name) {
          const parts = data.interpreter_name.split(' ');
          nombres = parts[0] || '';
          apellido_paterno = parts[1] || '';
          apellido_materno = parts.slice(2).join(' ') || '';
        }
        setForm({
          ...EMPTY,
          nombres: nombres || '', apellido_paterno: apellido_paterno || '', apellido_materno: apellido_materno || '',
          email: p.email || data.email || '',
          phone: p.phone || '', city: p.city || '',
          linkedin_url: p.linkedin_url || '', website: p.website || '',
          bio: p.bio || '', photo_url: p.photo_url || '', references_text: p.references_text || '',
          additional_emails: p.additional_emails || [], additional_phones: p.additional_phones || [],
          excluded_language_pairs: p.excluded_language_pairs || [],
          active_languages: p.active_languages || [], passive_languages: p.passive_languages || [],
          specialty: p.specialty || [], modalities: p.modalities || [],
        });
        setState({ status: 'ok', data });
      })
      .catch((e) => alive && setState({ status: 'invalid', message: e.response?.data?.detail || 'Esta invitación no es válida.' }));
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    api.get('/auth/registration-catalogs').then(({ data }) => setCatalogs(data || { languages: [], specialties: [], modalities: [] })).catch(() => {});
  }, []);

  // ── Autosave: restore locally-saved draft once the invitation is valid ──
  useEffect(() => {
    if (!DRAFT_KEY || state.status !== 'ok') return;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft && draft.form) {
        setForm((f) => ({ ...f, ...draft.form }));
        setRestoredAt(draft.savedAt || Date.now());
        log('draft_restored', { savedAt: draft.savedAt });
      }
    } catch { /* ignore corrupt draft */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status]);

  // Persist a draft as the user types (excludes password fields for safety).
  useEffect(() => {
    if (!DRAFT_KEY || state.status !== 'ok') return;
    const id = setTimeout(() => {
      try {
        const { password, confirm, ...rest } = form;
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ form: rest, savedAt: Date.now() }));
      } catch { /* storage unavailable */ }
    }, 600);
    return () => clearTimeout(id);
  }, [form, DRAFT_KEY, state.status]);

  function discardDraft() {
    if (DRAFT_KEY) { try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ } }
    setRestoredAt(null);
    window.location.reload();
  }

  const fullName = useMemo(() => [form.nombres, form.apellido_paterno, form.apellido_materno].filter(Boolean).join(' ').trim(), [form.nombres, form.apellido_paterno, form.apellido_materno]);
  const usernameSuggestion = useMemo(() => {
    const slug = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '');
    const n = slug((form.nombres || '').split(' ')[0]);
    const a = slug((form.apellido_paterno || '').split(' ')[0]);
    return n && a ? `${n}.${a}` : '';
  }, [form.nombres, form.apellido_paterno]);

  // Live username availability check (debounced).
  useEffect(() => {
    const u = (form.username || '').trim();
    if (u.length < 3) { setUsernameCheck({ checking: false, available: null, suggestions: [] }); return; }
    setUsernameCheck((c) => ({ ...c, checking: true }));
    const id = setTimeout(async () => {
      try {
        const { data } = await api.get('/auth/username-available', { params: { username: u } });
        setUsernameCheck({ checking: false, available: data.available, suggestions: data.suggestions || [] });
      } catch {
        setUsernameCheck({ checking: false, available: null, suggestions: [] });
      }
    }, 450);
    return () => clearTimeout(id);
  }, [form.username]);
  const previewForm = useMemo(() => ({ ...form, full_name: fullName, membership_status: 'active' }), [form, fullName]);

  async function submit(e) {
    e.preventDefault();
    setError('');
    if (!form.nombres.trim() || !form.apellido_paterno.trim()) { setError('Nombre y apellido paterno son obligatorios.'); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    if (form.username.trim().length < 3) { setError('El usuario debe tener al menos 3 caracteres.'); return; }
    if (usernameCheck.available === false) { setError('Ese usuario ya está en uso. Elige uno de los sugeridos o escribe otro.'); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    if (form.password.length < 8) { setError('La contraseña debe tener al menos 8 caracteres.'); return; }
    if (form.password !== form.confirm) { setError('Las contraseñas no coinciden.'); return; }
    setSubmitting(true);
    try {
      await api.post('/invitations/accept', {
        token,
        username: form.username.trim().toLowerCase(),
        password: form.password,
        profile: {
          nombres: form.nombres.trim(), apellido_paterno: form.apellido_paterno.trim(), apellido_materno: form.apellido_materno.trim(),
          full_name: fullName, email: form.email.trim(), phone: form.phone.trim(), city: form.city.trim(),
          additional_emails: form.additional_emails, additional_phones: form.additional_phones,
          linkedin_url: form.linkedin_url.trim(), website: form.website.trim(), bio: form.bio,
          references_text: form.references_text,
          photo_url: form.photo_url, excluded_language_pairs: form.excluded_language_pairs,
          active_languages: form.active_languages, passive_languages: form.passive_languages,
          specialty: form.specialty, modalities: form.modalities,
        },
      });
      setState({ status: 'done' });
      if (DRAFT_KEY) { try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ } }
      setTimeout(() => navigate('/login'), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || 'No pudimos crear tu cuenta. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Simple states ──────────────────────────────────────────────────────────
  if (state.status === 'loading' || state.status === 'invalid' || state.status === 'done') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-charcoal-50 via-white to-cmic-50 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md text-center" data-testid="invitation-accept-page">
          <img src={logo} alt="CMIC" className="h-14 mx-auto mb-4" />
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            {state.status === 'loading' && <div className="py-6 text-charcoal-400 animate-pulse">Verificando invitación…</div>}
            {state.status === 'invalid' && (
              <>
                <div className="w-14 h-14 mx-auto rounded-full bg-rose-50 flex items-center justify-center mb-3"><AlertCircle size={28} className="text-rose-600" /></div>
                <h2 className="font-semibold text-charcoal-800 mb-1">Invitación no válida</h2>
                <p className="text-sm text-charcoal-500 mb-4">{state.message}</p>
                <Link to="/" className="text-cmic-600 hover:text-cmic-700 text-sm font-medium">Ir al sitio principal</Link>
              </>
            )}
            {state.status === 'done' && (
              <div data-testid="invitation-accept-success">
                <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-3"><CheckCircle2 size={28} className="text-emerald-600" /></div>
                <h2 className="font-semibold text-charcoal-800 mb-1">¡Cuenta creada!</h2>
                <p className="text-sm text-charcoal-500">Ya puedes iniciar sesión. Te llevamos al inicio de sesión…</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Pre-filled review form ───────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-charcoal-50 via-white to-cmic-50 py-8 px-4">
      <div className="max-w-5xl mx-auto" data-testid="invitation-accept-page" onClickCapture={onClickCapture}>
        <div className="text-center mb-6">
          <img src={logo} alt="CMIC" className="h-12 mx-auto mb-3" />
          <h1 className="text-2xl sm:text-3xl font-bold text-charcoal-900">Revisa tu perfil y crea tu cuenta</h1>
          <p className="text-sm text-charcoal-500 mt-1">Hola <strong>{state.data.interpreter_name}</strong>, ya cargamos tu información. Revísala, corrige lo que haga falta y elige tu acceso.</p>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6 flex items-start gap-2 text-sm text-amber-800">
          <ShieldCheck size={16} className="flex-shrink-0 mt-0.5" />
          <p>Tu cuenta se activa de inmediato. Los cambios a <strong>idiomas, especialidades y modalidades</strong> quedan sujetos a la <strong>aprobación del consejo</strong> y no se mostrarán en el directorio hasta ser aprobados.</p>
        </div>

        <form onSubmit={submit} className="grid lg:grid-cols-[1fr_380px] gap-6 items-start">
          <div className="space-y-5">
            {restoredAt && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800 flex items-start justify-between gap-3" data-testid="invitation-draft-restored">
                <div className="flex gap-2">
                  <ShieldCheck size={14} className="flex-shrink-0 mt-0.5 text-amber-600" />
                  <span>Recuperamos los cambios que habías hecho. Por seguridad, vuelve a escribir tu contraseña.</span>
                </div>
                <button type="button" onClick={discardDraft} data-testid="invitation-draft-discard" className="flex-shrink-0 font-semibold text-amber-700 hover:text-amber-900 underline">
                  Descartar cambios
                </button>
              </div>
            )}
            {error && <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-sm text-rose-700" data-testid="invitation-error">{error}</div>}

            <Card n="1" title="Datos personales">
              <div className="mb-5">
                <label className="block text-xs font-semibold text-charcoal-700 mb-2 flex items-center gap-1.5"><User size={13} className="text-cmic-500" />Foto de perfil</label>
                <ImageUploader
                  value={form.photo_url ? absoluteUrl(form.photo_url) : ''}
                  onChange={(url) => set('photo_url', url)}
                  testid="inv-photo"
                  shape="circle"
                  uploadEndpoint="/invitations/upload-photo"
                  uploadExtra={{ token }}
                  hint="Opcional. JPG, PNG o WebP. Te dejaré recortarla antes de guardar."
                />
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Nombre(s)" icon={User} required value={form.nombres} onChange={(e) => set('nombres', e.target.value)} data-testid="inv-nombres" />
                <Field label="Apellido paterno" required value={form.apellido_paterno} onChange={(e) => set('apellido_paterno', e.target.value)} data-testid="inv-apellido-paterno" />
                <Field label="Apellido materno" value={form.apellido_materno} onChange={(e) => set('apellido_materno', e.target.value)} data-testid="inv-apellido-materno" />
              </div>
            </Card>

            <Card n="2" title="Elige tu acceso" subtitle="Usuario y contraseña para iniciar sesión.">
              <div className="bg-cmic-50 border border-cmic-100 rounded-xl px-4 py-3 mb-4 text-sm text-cmic-800 leading-relaxed">
                Aquí defines cómo entrarás a tu cuenta. El <strong>usuario</strong> es un nombre corto para iniciar sesión (<u>no es tu correo</u>) y la <strong>contraseña</strong> es tu clave secreta. Los escribirás juntos cada vez que entres, así que te recomendamos anotarlos en un lugar seguro. Sugerencia: usa tu nombre y tu apellido separados por un punto.
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-charcoal-700 mb-1.5 flex items-center gap-1.5"><User size={13} className="text-cmic-500" />Usuario<span className="text-rose-500">*</span></label>
                  <input
                    value={form.username}
                    onChange={(e) => set('username', e.target.value)}
                    data-testid="invitation-username-input"
                    placeholder="ej. alvaro.diego"
                    autoCapitalize="none"
                    className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30"
                  />
                  {form.username.trim().length >= 3 && usernameCheck.checking && (
                    <p className="mt-1.5 text-xs text-charcoal-400 flex items-center gap-1.5" data-testid="username-checking"><Loader2 size={12} className="animate-spin" /> Verificando disponibilidad…</p>
                  )}
                  {!usernameCheck.checking && usernameCheck.available === true && form.username.trim().length >= 3 && (
                    <p className="mt-1.5 text-xs text-emerald-600 font-medium flex items-center gap-1.5" data-testid="username-available"><CheckCircle2 size={13} /> ¡Usuario disponible!</p>
                  )}
                  {!usernameCheck.checking && usernameCheck.available === false && (
                    <div className="mt-1.5" data-testid="username-taken">
                      <p className="text-xs text-rose-600 font-medium flex items-center gap-1.5"><AlertCircle size={13} /> Ese usuario ya está en uso. Prueba con otro:</p>
                      {usernameCheck.suggestions.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {usernameCheck.suggestions.map((s) => (
                            <button key={s} type="button" onClick={() => set('username', s)} data-testid={`username-alt-${s}`}
                              className="text-xs font-semibold text-cmic-700 bg-cmic-50 hover:bg-cmic-100 border border-cmic-100 rounded-lg px-2.5 py-1 transition-colors">{s}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {usernameSuggestion && form.username.trim().toLowerCase() !== usernameSuggestion && usernameCheck.available !== false && (
                    <button
                      type="button"
                      onClick={() => set('username', usernameSuggestion)}
                      data-testid="invitation-username-suggestion"
                      className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-cmic-700 bg-cmic-50 hover:bg-cmic-100 border border-cmic-100 rounded-lg px-2.5 py-1.5 transition-colors text-left"
                    >
                      <Sparkles size={12} className="flex-shrink-0" />
                      <span>Sugerencia de usuario: <strong>{usernameSuggestion}</strong> · toca para usarla</span>
                    </button>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-charcoal-700 mb-1.5 flex items-center gap-1.5"><Lock size={13} className="text-cmic-500" />Contraseña<span className="text-rose-500">*</span></label>
                  <div className="relative">
                    <input type={showPwd ? 'text' : 'password'} value={form.password} onChange={(e) => set('password', e.target.value)} minLength={8} data-testid="invitation-password-input" placeholder="Mín. 8" className="w-full rounded-xl border border-gray-200 px-3 pr-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30" />
                    <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-charcoal-400 hover:text-charcoal-700">{showPwd ? <EyeOff size={15} /> : <Eye size={15} />}</button>
                  </div>
                </div>
                <Field label="Confirma contraseña" required type={showPwd ? 'text' : 'password'} value={form.confirm} onChange={(e) => set('confirm', e.target.value)} data-testid="invitation-confirm-input" />
              </div>
            </Card>

            <Card n="3" title="Datos de contacto">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Correo principal" icon={Mail} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="inv-email" />
                <Field label="Teléfono principal" icon={Phone} value={form.phone} onChange={(e) => set('phone', e.target.value)} data-testid="inv-phone" />
                <Field label="Ciudad / país" icon={MapPin} value={form.city} onChange={(e) => set('city', e.target.value)} data-testid="inv-city" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <TagSelect label="Correos adicionales" icon={Mail} value={form.additional_emails} onChange={(v) => set('additional_emails', v)} options={[]} placeholder="otro@correo.com (Enter)" testid="inv-additional-emails" />
                <TagSelect label="Teléfonos adicionales" icon={Phone} value={form.additional_phones} onChange={(v) => set('additional_phones', v)} options={[]} placeholder="+52 … (Enter)" testid="inv-additional-phones" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <Field label="LinkedIn" icon={Linkedin} value={form.linkedin_url} onChange={(e) => set('linkedin_url', e.target.value)} data-testid="inv-linkedin" placeholder="https://linkedin.com/in/…" />
                <Field label="Página web" icon={Globe} value={form.website} onChange={(e) => set('website', e.target.value)} data-testid="inv-website" placeholder="https://…" />
              </div>
            </Card>

            <Card n="4" title="Idiomas, especialidades y modalidades" subtitle="Los cambios aquí requieren aprobación del consejo.">
              <div className="space-y-4">
                <TagSelect label="Idiomas activos" icon={Languages} value={form.active_languages} onChange={(v) => set('active_languages', v)} options={catalogs.languages} placeholder="Escribe y presiona Enter…" testid="inv-active-languages" />
                <TagSelect label="Idiomas pasivos" icon={Languages} value={form.passive_languages} onChange={(v) => set('passive_languages', v)} options={catalogs.languages} placeholder="Escribe y presiona Enter…" testid="inv-passive-languages" />
                <TagSelect label="Especialidades" icon={Briefcase} value={form.specialty} onChange={(v) => set('specialty', v)} options={catalogs.specialties} placeholder="Ej. Médico, Jurídico…" testid="inv-specialties" />
                <TagSelect label="Modalidades" icon={Mic} value={form.modalities} onChange={(v) => set('modalities', v)} options={catalogs.modalities} placeholder="Elige o escribe…" testid="inv-modalities" />
              </div>
            </Card>

            <Card n="5" title="Semblanza" subtitle="Tu biografía profesional como aparecerá en el directorio.">
              <RichTextEditor value={form.bio} onChange={(html) => set('bio', html)} testid="inv-bio" minHeight={160} allowMedia={false} />
            </Card>

            <Card n="6" title="Certificaciones" subtitle="Se mostrarán numeradas (1, 2, 3…) en tu perfil público. Una certificación por casilla.">
              <CertificationsField value={form.references_text} onChange={(v) => set('references_text', v)} />
            </Card>

            <button type="submit" disabled={submitting} data-testid="invitation-submit-btn" className="w-full inline-flex items-center justify-center gap-2 bg-cmic-600 text-white rounded-xl px-4 py-3.5 text-sm font-semibold hover:bg-cmic-700 disabled:opacity-50 shadow-sm">
              {submitting ? 'Creando cuenta…' : (<><UserPlus size={16} /> Crear mi cuenta y guardar perfil<ArrowRight size={15} /></>)}
            </button>
            <p className="text-center text-xs text-charcoal-400">¿Ya tienes cuenta? <Link to="/login" className="text-cmic-600 hover:text-cmic-700">Inicia sesión</Link></p>
            <div className="text-center pt-1">
              <SupportContactButton
                source="invitation"
                defaultEmail={form.email || state.data?.email || ''}
                defaultName={fullName || state.data?.interpreter_name || ''}
                getDiagnostics={() => ({ error_message: error, click_log: logRef.current })}
              />
            </div>
          </div>

          <div className="lg:sticky lg:top-6">
            <button type="button" onClick={() => setShowFullPreview(true)} data-testid="open-full-preview-btn" className="w-full mb-3 inline-flex items-center justify-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white text-sm font-semibold rounded-xl px-4 py-2.5 transition-colors shadow-sm">
              <Eye size={16} /> Ver previsualización final
            </button>
            <InterpreterProfilePreview form={previewForm} />
          </div>
        </form>
      </div>

      {showFullPreview && <FullProfilePreview form={previewForm} onClose={() => setShowFullPreview(false)} />}
    </div>
  );
}
